'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { Search, Loader2, Plus, BookOpen, TrendingUp, BarChart2, ArrowRight } from 'lucide-react';

type StrategyCard = {
  id: string;
  title: string;
  description: string | null;
  tags: string[];
  linkCount: number;
  commentCount: number;
  saveCount: number;
  updatedAt: string;
  creatorUsername: string;
  viewerSaved?: boolean;
};

export default function StrategyBrowseClient() {
  const { data: session } = useSession();
  const [strategies, setStrategies] = useState<StrategyCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('newest');
  const [creator, setCreator] = useState('');
  const [tag, setTag] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '48', sort });
      if (q.trim()) params.set('q', q.trim());
      if (creator.trim()) params.set('creator', creator.trim());
      if (tag.trim()) params.set('tag', tag.trim());
      const res = await fetch(`/api/strategies?${params.toString()}`);
      const data = await res.json();
      setStrategies(data.strategies ?? []);
    } catch {
      setStrategies([]);
    } finally {
      setLoading(false);
    }
  }, [q, sort, creator, tag]);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    void loadRef.current();
  }, [sort]);

  return (
    <div className="min-h-screen bg-black pt-20 pb-16">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-amber-400 text-xs font-semibold uppercase tracking-widest mb-2">
              <BookOpen className="h-3.5 w-3.5" />
              Trading Playbooks
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Strategy Library</h1>
            <p className="text-zinc-400 text-sm">
              Named trading playbooks from the community. Each strategy documents how to find stocks, enter, manage risk, and exit.
              Link your journal trades to any strategy to track your win rate per playbook.
            </p>
          </div>
          {session?.user ? (
            <Link
              href="/strategy/new"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold px-4 py-2.5 shrink-0"
            >
              <Plus className="h-4 w-4" />
              New strategy
            </Link>
          ) : null}
        </div>

        <div className="flex flex-col lg:flex-row gap-3 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadRef.current()}
              placeholder="Search title or description"
              className="w-full rounded-xl bg-zinc-900 border border-zinc-700 pl-10 pr-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-xl bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="newest">Newest</option>
            <option value="used">Most used (linked trades)</option>
            <option value="comments">Most commented</option>
            <option value="saves">Most saved</option>
          </select>
          <input
            value={creator}
            onChange={(e) => setCreator(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadRef.current()}
            placeholder="Creator username"
            className="rounded-xl bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
          <input
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadRef.current()}
            placeholder="Tag"
            className="rounded-xl bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
          <button
            type="button"
            onClick={() => loadRef.current()}
            className="rounded-xl bg-zinc-800 border border-zinc-600 px-4 py-2.5 text-white hover:bg-zinc-700"
          >
            Apply
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-10 w-10 text-amber-400 animate-spin" />
          </div>
        ) : strategies.length === 0 ? (
          <div className="text-center py-20 text-zinc-500 border border-zinc-800 rounded-2xl">
            <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium mb-1">No strategies match your filters</p>
            <p className="text-sm">Try clearing the search or tag filter, or create the first one.</p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2">
            {strategies.map((s) => (
              <Link
                key={s.id}
                href={`/strategy/${s.id}`}
                className="group block rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 hover:border-amber-500/40 hover:bg-zinc-900/80 transition-all duration-200"
              >
                {/* Title row */}
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h2 className="text-base font-semibold text-white group-hover:text-amber-300 transition-colors leading-snug">
                    {s.title}
                  </h2>
                  {s.viewerSaved ? (
                    <span className="text-xs text-amber-400 shrink-0 pt-0.5">Saved</span>
                  ) : null}
                </div>

                {/* Creator */}
                <p className="text-xs text-zinc-500 mb-3">by {s.creatorUsername}</p>

                {/* Description */}
                {s.description ? (
                  <p className="text-sm text-zinc-400 line-clamp-2 mb-4 leading-relaxed">{s.description}</p>
                ) : null}

                {/* Tags */}
                {s.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {s.tags.slice(0, 6).map((t) => (
                      <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700/50">
                        {t}
                      </span>
                    ))}
                    {s.tags.length > 6 ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-500">+{s.tags.length - 6}</span>
                    ) : null}
                  </div>
                ) : null}

                {/* Stats + CTA */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-wrap gap-3 text-xs text-zinc-600">
                    {s.linkCount > 0 ? (
                      <span className="flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" />
                        {s.linkCount} trade{s.linkCount !== 1 ? 's' : ''}
                      </span>
                    ) : null}
                    {s.saveCount > 0 ? (
                      <span className="flex items-center gap-1">
                        <BarChart2 className="h-3 w-3" />
                        {s.saveCount} saved
                      </span>
                    ) : null}
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-amber-400/70 group-hover:text-amber-400 transition-colors shrink-0">
                    View playbook <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
